/**
 * Supervision tab — everything about working under a supervisor in one place:
 *   - Note co-signing requirement (commits via the footer Save button)
 *   - Supervisory claim submission (autosaves; bill-under NPI overrides)
 */

import { Box, Divider, FormControlLabel, Switch, Typography } from '@mui/material';
import type { MemberBillingInfo } from '@store/generated/organizations';

import { ClaimSubmissionSettings } from './ClaimSubmissionSettings';

interface SupervisionSectionProps {
  userId: string;
  billingInfo?: MemberBillingInfo | null;
  isClinician: boolean;
  needsCosign: boolean;
  onNeedsCosignChange: (value: boolean) => void;
  disabled?: boolean;
}

export function SupervisionSection({
  userId,
  billingInfo,
  isClinician,
  needsCosign,
  onNeedsCosignChange,
  disabled = false,
}: SupervisionSectionProps) {
  return (
    <Box>
      <Box>
        <Typography variant="subtitle2" color="text.secondary">
          Note Co-Signing
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.5, mb: 1 }}
        >
          Applies on Save.
        </Typography>
        {isClinician ? (
          <FormControlLabel
            control={
              <Switch
                checked={needsCosign}
                onChange={e => onNeedsCosignChange(e.target.checked)}
                disabled={disabled}
                size="small"
              />
            }
            label={
              <Box>
                <Typography variant="body2">Require co-signature</Typography>
                <Typography variant="caption" color="text.secondary">
                  Notes must be co-signed by a supervisor before being finalized
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start' }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Co-signature requirements apply to members with the Clinician role.
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      <ClaimSubmissionSettings userId={userId} billingInfo={billingInfo} disabled={disabled} />
    </Box>
  );
}
